import React from 'react';
import { CalendarDays, Users, Repeat, PauseCircle, Archive } from 'lucide-react';
import { formatEventDate, timeLabel } from '../../lib/recurrence';

export const StatusBadge = ({ status, recurring }) => {
  if (status === 'archived') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 text-gray-300" data-testid="event-badge-archived">
        <Archive className="w-3 h-3" /> Archived
      </span>
    );
  }
  if (status === 'paused') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FFB800]/15 text-[#FFB800]" data-testid="event-badge-paused">
        <PauseCircle className="w-3 h-3" /> Paused
      </span>
    );
  }
  if (status === 'past') {
    return (
      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-gray-500" data-testid="event-badge-past">
        Ended
      </span>
    );
  }
  if (recurring) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#9D00FF]/20 text-[#d9a3ff]" data-testid="event-badge-recurring">
        <Repeat className="w-3 h-3" /> Recurring
      </span>
    );
  }
  return null;
};

export const EventCard = ({ ev, onClick }) => {
  const recurring = !!ev.recurrence;
  const dateLine = recurring
    ? ev.recurrence_label
    : formatEventDate(ev.event_date, { day: 'numeric', month: 'short', year: 'numeric' });
  const time = timeLabel(ev);
  return (
    <button
      onClick={onClick}
      className="bg-white/[0.03] border border-white/10 hover:border-[#FF007F]/50 rounded-2xl p-5 text-left transition-colors group relative"
      data-testid={`event-card-${ev.id}`}
    >
      <div className="absolute top-3 left-3 z-10">
        <StatusBadge status={ev.status} recurring={recurring} />
      </div>
      {ev.poster_url ? (
        <img src={ev.poster_url} alt={ev.name} className="w-full aspect-video object-cover rounded-xl mb-3 border border-white/5" />
      ) : (
        <div className="w-full aspect-video rounded-xl bg-gradient-to-br from-[#FF007F]/30 to-[#9D00FF]/30 flex items-center justify-center mb-3 border border-white/5">
          {recurring ? <Repeat className="w-10 h-10 text-white/40" /> : <CalendarDays className="w-10 h-10 text-white/40" />}
        </div>
      )}
      <p className="text-white font-bold text-lg truncate group-hover:text-[#FF007F] transition-colors">{ev.name}</p>
      <p className="text-gray-400 text-xs mt-1">
        {dateLine}{time ? ` · ${time}` : ''}
      </p>
      {recurring && ev.next_date && ev.status === 'upcoming' && (
        <p className="text-[#d9a3ff] text-xs mt-1" data-testid={`event-next-date-${ev.id}`}>
          Next: {formatEventDate(ev.next_date)}
        </p>
      )}
      <p className="text-[#00FFFF] text-xs font-semibold mt-3 flex items-center gap-1">
        <Users className="w-3.5 h-3.5" />
        {ev.registration_count || 0} registered{recurring && ev.status === 'upcoming' ? ' for next' : ''}
        {ev.checked_in_count > 0 && <span className="text-[#39FF14] ml-1">· {ev.checked_in_count} in</span>}
      </p>
    </button>
  );
};
